import { useMutation, useQueryClient } from '@tanstack/react-query';

import { campaignsEndpoints as endpoints } from '@/api/endpoints';
import { CampaignSubscriber, CampaignSubscriberWithJourney } from '@/shared/types/campaigns';

import { API } from './useCampaignSubscribers';

// Mirror the server's stop write (lifecycle 'stopped' + skip_fallback) for instant optimistic feedback.
const markStopped = <T extends CampaignSubscriber>(subscriber: T): T => ({
  ...subscriber,
  lifecycle_status: 'stopped',
  metadata: { ...(subscriber.metadata as Record<string, unknown>), skip_fallback: true },
});

export const useStopCampaignSubscriber = () => {
  const queryClient = useQueryClient();

  const { mutateAsync: stopSubscriberAsync, status: stopSubscriberStatus } = useMutation({
    mutationFn: ({ campaignId, subscriberId }: { campaignId: string; subscriberId: string }) =>
      API.stopCampaignSubscriber(campaignId, subscriberId),
    onSuccess: (_data, variables) => {
      // Update the subscribers list cache
      queryClient.invalidateQueries({
        queryKey: [endpoints.campaignSubscribers(variables.campaignId)],
      });

      // Update the single subscriber cache
      queryClient.setQueryData<CampaignSubscriberWithJourney>(
        [endpoints.campaignSubscriber(variables.campaignId, variables.subscriberId)],
        (prev) =>
          prev
            ? {
                ...markStopped(prev),
                next_action: prev.next_action && { state: 'stopped', stop_reason: 'manual' },
                contact_journey: [
                  ...(prev.contact_journey ?? []),
                  {
                    status_change: {
                      action: 'stopped',
                      reason: 'manual',
                      previous_status: prev.lifecycle_status ?? null,
                      actor_user_id: null,
                      timestamp: Date.now(),
                    },
                  },
                ],
              }
            : prev
      );
    },
    meta: { errorToast: true },
  });

  return { stopSubscriberAsync, stopSubscriberStatus };
};
